// Builds a DXF (R12) from the current drawing for "File → Export to DXF".
//
// Everything is collected in world millimetres. The objects are written in the
// stacking the canvas paints, one emitter per kind of object (`DXF_EMITTERS`, a
// Record over ItemKind, as render()'s paint switch is exhaustive), each on the
// DXF layer named after its layer. Object geometry is taken from the same
// functions the renderer uses (wall bands, benches, doors, poly-room walls,
// imported DXF entities), so the export matches what's on screen. Filled areas
// (walls, benches, product footprints, user shapes, imported hatches) emit a DXF
// HATCH plus an outline; strokes emit LINE/LWPOLYLINE.

import {
  glazingLook,
  glazingOfPane,
  glazingPlanCut,
  glazingSwing,
  roomGlazings,
} from './roomGlazing';
import {
  drawingDimStyle,
  formatDimension,
} from './dimFormat';
import {
  textBoxCorners,
  textBoxToWorld,
} from './textBox';
import type {
  Arc,
  CeilingPlanView,
  Connection,
  DimensionLine,
  DimStyle,
  Drawing,
  FloorDrain,
  Flooring,
  GeomEntity,
  Leader,
  Polygon,
  ProductInstance,
  RevisionCloud,
  SectionLine,
  TextComment,
  Vec2,
  Wall,
} from '../shared/types';
import {
  buildDxf,
  type DxfEntity,
  type DxfLayer,
  rgbToAci,
} from '../shared/dxf';
import {
  CANALINA_INSET,
  CANALINA_STROKE,
  COMMENT_FONT_MM,
  DRAIN_COLOR,
  instanceCorners,
  REVCLOUD_COLOR,
  saltWallHull,
} from './render';
import {
  glassPolysLocal,
  openingBandLocal,
} from './glass';
import { roomDoors } from './door';
import {
  BENCH_END_LINE,
  closedEndLines,
  LIP_SHADE,
  roomBenchSeats,
  saunaBenchPlan,
  structureColor,
  TRESTLE_LINE_SHADE,
} from './benchSolids';
import { darkenHex } from './hexTone';
import {
  backrestColor,
  backrestPlan,
  backrestsOf,
} from './backrest';
import {
  heaterWallBands,
  isPolyRoom,
  openingHoleBand,
  polyInnerWallFill,
  polyWall,
  roomWallCuts,
} from './polyRoom';
import {
  wallDoorSwing,
  wallFeatureQuad,
  wallFeatureSpans,
  wallPaneQuad,
  wallPocketQuad,
  wallPockets,
  wallSegFrame,
  wallSolidRuns,
} from './wallFeatures';
import {
  type InWallPart,
  inWallParts,
} from './wallCuts';
import {
  partRisers,
  type Riser,
  riserRing,
  type RiserWater,
} from './risers';
import { splinePoints } from './curves';
import { numberedNames } from './naming';
import { productName } from '../shared/names';
import {
  type ConnectionShow,
  connectionShown,
  connGeom,
  pipeTubes,
} from './connections';
import {
  CEILING_ARROW_MM,
  ceilingMarksShown,
  isFlatCeiling,
} from './ceiling';
import {
  sectionLabel,
  type SectionPipe,
  segmentPolyIntervals,
} from './section';
import { sectionToDxf } from './dxfSection';
import { partHeight } from './heights';
import {
  apply as applyMat3,
  bodyOf,
  bodyPoint,
  isTilted,
  planFaces,
} from './tilt';
import {
  modelPlan,
  modelPlanLines,
  uprightModelPlan,
} from './partModel';
import {
  type PartAttachment,
  partLook,
} from './partLook';
import { wallBoundaryEdges } from './wall';
import { unreachable } from '../shared/exhaustive';
import {
  lightingLegendRows,
  lightingRows,
  scheduleStrings,
} from './schedule';
import {
  DXF_SUB_LAYERS,
  DXF_USED_SUB_LAYERS,
  dxfLayerNames,
  type ItemKind,
  type LayerItem,
  type LayerModel,
  layerObjectIds,
  stackOrder,
  TABLE_IDS,
  type TableKey,
  withUsedSubLayers,
} from './layers';
import type { KindObjects } from './drawingObjects';
import {
  lightingLegendDxf,
  lightKeysInOrder,
  lightLines,
  lightTagAnchors,
  lightTags,
  tagLeader,
  tagTextAt,
  updatedTagOrder,
} from './lights';
import {
  type RcpPaint,
  rcpToDxf,
} from './rcp';
import {
  cloudScallops,
  distToSegment,
  HEATER_BODY_COLOR,
  instanceToWorld,
  pointInPolygon,
  REVCLOUD_SCALLOP_MM,
  rotatePoint,
} from './geometry';
import {
  eachStippleMark,
  FLOOR_HATCH_MM,
  FLOOR_STYLE,
} from './floorHatch';
import {
  FALL_ARROW_MM,
  fallArrows,
  floorIsSloping,
} from './flooring';
import { grilleLayout } from './floorGrille';
import { roomDoor } from './roomDoor';
import {
  type DoorSwing,
  mapSwing,
  swingDxfArc,
} from './doorSwing';

export interface DxfExportOptions {
  /** The drawing's layers and their ONE stacking (layers.ts `layerModel`, the
   *  model the canvas paints): each object goes on the DXF layer named after its
   *  layer (a room's benches, panes and doors on `<layer>-BENCHES` / `-GLASS` /
   *  `-DOORS`), bottom of the stacking first — a hidden layer's objects too, on
   *  its layer switched off. */
  layers: LayerModel;
  /** Include the imported DXF plan (`drawing.background`) in the export. */
  includeBackground: boolean;
  /** Product categories hidden by a discipline toggle — skipped so the export
   *  matches what's on screen. */
  hiddenCategories?: Set<string>;
  /** View ▸ Show ceilings: every shaped room's ceiling lines and arrows, on the
   *  room's `<layer>-CEILING`. */
  showCeilings?: boolean;
  /** What the sections cut and show (every object not on a hidden layer, as the
   *  canvas draws them). Absent = the whole drawing. */
  sectionSources?: SectionSources;
  /** A reflected ceiling plan's words and colour as the canvas paints them: its
   *  title, its figures in the dimension style, its room's wall colour. Absent =
   *  no title, bare millimetres, the sauna walls' default. */
  rcpPaint?: (v: CeilingPlanView, room: ProductInstance) => Pick<RcpPaint, 'wall' | 'title' | 'figure'>;
  /** false: the Lighting toggle hides the lights — a room's and its ceiling
   *  plans' lights and their tags. */
  lights?: boolean;
  /** false: the Flooring toggle hides the floorings. */
  floorings?: boolean;
  /** The connection lines as the canvas shows them (`connectionShown`): none
   *  while the schedule is off, none of a discipline its toggle hides. Absent =
   *  every connection. */
  connections?: ConnectionShow;
  /** A floor's drain as the part the plan draws for it (its product's size), or
   *  null. Absent = the floors are written without their drains. */
  drainOf?: (drain: FloorDrain) => ProductInstance | null;
}

type SectionSources = { instances: ProductInstance[]; walls: Wall[]; polygons: Polygon[]; pipes?: SectionPipe[] };

/** What a room's lights need from the drawing: its parts by id (a fibre kit's
 *  projector) and the lights' tags by tag key. */
interface LightExport {
  parts: ReadonlyMap<string, ProductInstance>;
  tags: ReadonlyMap<string, string>;
}

/** One export's shared facts, worked out once per call (nothing outlives it). */
interface DxfCtx {
  drawing: Drawing;
  opts: DxfExportOptions;
  /** Auto-numbered part labels, matching the on-screen drawing (Sauna01, …). */
  names: ReadonlyMap<string, string>;
  /** The parts set into walls (wallCuts.ts), read by the rooms' walls. */
  cuts: readonly InWallPart[];
  /** The water risers of the parts set into rooms' walls (risers.ts). */
  risers: readonly Riser[];
  parts: ReadonlyMap<string, ProductInstance>;
  tags: ReadonlyMap<string, string>;
  /** Null while the Lighting toggle hides the lights. */
  lit: LightExport | null;
  src: SectionSources;
  /** A part a discipline toggle hides on screen. */
  hidden: (inst: ProductInstance) => boolean;
}

/** How one kind of drawing object is written. `add` puts an entity on the
 *  object's layer: an entity's `layer` names one of its sub-layers
 *  (`DXF_SUB_LAYERS`: `BENCHES` …, written `<layer>-BENCHES`) or is ignored. */
type DxfEmitter<T> = (obj: T, item: LayerItem, cx: DxfCtx, add: (e: DxfEntity) => void) => void;

/** The plan's DXF, one emitter per kind of drawing object, as the canvas's
 *  paint switch has one painter per kind: a new `ItemKind` does not compile until
 *  it says how it is written (a kind that writes nothing says why). */
const DXF_EMITTERS: { [K in ItemKind]: DxfEmitter<KindObjects[K]> } = {
  arc: (a, _it, _cx, add) => emitArc(a, add),
  cloud: (c, _it, _cx, add) => emitCloud(c, add),
  conn: (c, _it, cx, add) => emitConnection(cx, c, add),
  dim: (d, _it, cx, add) => emitDimension(d, drawingDimStyle(cx.drawing), add),
  flooring: (f, _it, cx, add) => emitFlooring(cx, f, add),
  instance: (inst, _it, cx, add) => {
    if (cx.hidden(inst)) return; // a discipline toggle hides this object on screen
    try { emitInstance(inst, add, cx); } catch { /* skip an object that can't be exported rather than fail the whole file */ }
  },
  leader: (l, _it, _cx, add) => emitLeader(l, add),
  polygon: (p, _it, _cx, add) => emitPolygon(p, 'SHAPES', add),
  rcp: (v, _it, cx, add) => emitRcp(cx, v, add),
  section: (sec, it, cx, add) => emitSection(cx, sec, it.idx, add),
  table: (_t, it, cx, add) => {
    const key = (Object.keys(TABLE_IDS) as TableKey[]).find((k) => TABLE_IDS[k] === it.id);
    if (key) TABLE_EMITTERS[key]?.(cx, add);
  },
  text: (t, _it, _cx, add) => emitText(t, add),
  wall: (w, _it, cx, add) => emitWall(w, add, cx.cuts),
};

/** A DXF has no tables. The Lighting table writes its legend (each tag's plan
 *  symbol, tag and light, where the table stands); the others are rows of
 *  names and figures with no drawing of their own, and write nothing. */
const TABLE_EMITTERS: Record<TableKey, ((cx: DxfCtx, add: (e: DxfEntity) => void) => void) | null> = {
  flooringTable: null,
  lightingTable: (cx, add) => { if (cx.lit) emitLightingLegend(cx.drawing, add); },
  pipeTable: null,
  preparationTable: null,
  scheduleTable: null,
};

/** A room's parts and a section's kinds of entity keep their own sub-layer
 *  under the object's layer. */
const SUB_LAYERS = new Set<string>(DXF_SUB_LAYERS);
/** Those declared only where something landed on them. */
const USED_SUBS = new Set<string>(DXF_USED_SUB_LAYERS.map((s) => s.sub));

const CONNECTION_COLORS: Record<string, string> = {
  electrical: '#ff8c1a', plumbing: '#4aa3ff', mechanical: '#9fb0c4', drainage: '#4db891',
};

const col = (hex?: string): number | undefined => (hex ? rgbToAci(hex) : undefined);
/** The water risers' colours in a DXF: CAD's own red and blue. */
const RISER_ACI: Record<RiserWater, number> = { hot: 1, cold: 5 };

/** Build the full DXF text for the drawing: the imported plan, then every
 *  object in the stacking the canvas paints (`stackOrder`), each by its kind's
 *  emitter, so a reader that paints in entity order paints it as the plan does. */
export function drawingToDxf(drawing: Drawing, opts: DxfExportOptions): string {
  const m = opts.layers;
  const names = dxfLayerNames(m.layers);
  const cx = exportContext(drawing, opts);
  const ents: DxfEntity[] = [];
  const used = new Map<string, Set<string>>();
  const on = (base: string) => (e: DxfEntity): void => {
    if (e.layer && USED_SUBS.has(e.layer)) { if (!used.has(base)) used.set(base, new Set()); used.get(base)!.add(e.layer); }
    e.layer = e.layer && SUB_LAYERS.has(e.layer) ? `${base}-${e.layer}` : base;
    ents.push(e);
  };
  if (opts.includeBackground && drawing.background) emitBackground(drawing.background.entities, drawing.backgroundOffset, on(names.get(m.layers[0].id)!));
  for (const it of stackOrder(m)) {
    const emit = DXF_EMITTERS[it.kind] as DxfEmitter<unknown>;
    emit(it.obj, it, cx, on(names.get(m.layerOf.get(it.id) ?? '') ?? '0'));
  }
  return buildDxf(ents, withUsedSubLayers(layerTable(m, drawing, names), used));
}

function exportContext(drawing: Drawing, opts: DxfExportOptions): DxfCtx {
  const lang = drawing.language ?? 'it';
  const parts = new Map(drawing.instances.map((i) => [i.id, i]));
  const tags = lightTags(updatedTagOrder(drawing.lightTagOrder, lightKeysInOrder(drawing.instances)));
  return {
    drawing,
    opts,
    names: numberedNames(drawing.instances, (i) => productName(i, lang)),
    cuts: inWallParts(drawing.instances),
    risers: partRisers(drawing.instances),
    parts,
    tags,
    lit: opts.lights === false ? null : { parts, tags },
    src: opts.sectionSources ?? { instances: drawing.instances, walls: drawing.walls || [], polygons: drawing.polygons || [] },
    hidden: (inst) => !!opts.hiddenCategories && opts.hiddenCategories.has(inst.category),
  };
}

/** The LAYER table: one DXF layer per layer (switched off when hidden, locked
 *  when locked), and the room parts' sub-layers on a layer that holds a room. */
function layerTable(m: LayerModel, drawing: Drawing, names: ReadonlyMap<string, string>): DxfLayer[] {
  const colour: Partial<Record<string, number>> = { imported: 8, shapes: 5, dimensions: 1, connections: 6 };
  const table: DxfLayer[] = [];
  for (const l of m.layers) {
    const name = names.get(l.id)!;
    const flags = { off: !!l.hidden, locked: !!l.locked };
    table.push({ name, color: (l.home && colour[l.home]) ?? 7, ...flags });
    const holdsRoom = layerObjectIds(m, l.id).some((id) => drawing.instances.find((i) => i.id === id)?.autoBenches);
    if (holdsRoom) {
      table.push({ name: `${name}-BENCHES`, color: 30, ...flags }, { name: `${name}-GLASS`, color: 4, ...flags }, { name: `${name}-DOORS`, color: 7, ...flags });
    }
  }
  return table;
}

/** A section's drawing beside its line (dxfSection.ts), `i` its place in the
 *  drawing's sections (its label). */
function emitSection(cx: DxfCtx, sec: SectionLine, i: number, add: (e: DxfEntity) => void): void {
  const { src, drawing } = cx;
  try { for (const e of sectionToDxf(sec, sectionLabel(i), src.instances, src.walls, src.polygons, src.pipes ?? pipeTubes(drawing, () => true))) add(e); } catch { /* skip a section that can't be exported rather than fail the whole file */ }
}

/** A reflected ceiling plan, while its room is in the drawing (and not hidden). */
function emitRcp(cx: DxfCtx, v: CeilingPlanView, add: (e: DxfEntity) => void): void {
  const { drawing, opts, src } = cx;
  const room = drawing.instances.find((i) => i.id === v.roomId);
  if (!room || cx.hidden(room) || !src.instances.includes(room)) return;
  const look = opts.rcpPaint?.(v, room) ?? { wall: '#e7b079', title: '', figure: (mm: number) => `${Math.round(mm)}` };
  const source = { drawing, keepPart: (p: ProductInstance) => !cx.hidden(p) && src.instances.includes(p) };
  const partName = (id: string): string => cx.names.get(id) ?? '';
  try { for (const e of rcpToDxf(v, room, { ...look, tags: cx.tags, lights: opts.lights, source, partName })) add(e); } catch { /* skip a view that can't be exported rather than fail the whole file */ }
}

/** The Lighting table's legend where the table stands (a DXF has no tables):
 *  its title, then each tag's plan symbol, tag and what it is. */
function emitLightingLegend(drawing: Drawing, add: (e: DxfEntity) => void): void {
  const t = drawing.lightingTable, rows = t ? lightingRows(drawing) : [];
  if (!t || !rows.length) return;
  const title = scheduleStrings(drawing.language ?? 'it').lightingTitle;
  for (const e of lightingLegendDxf(lightingLegendRows(rows).map((r) => ({ tag: r.tag, symbol: r.symbol, text: r.light })), { x: t.x, y: t.y }, t.heightMm ?? COMMENT_FONT_MM, title)) add(e);
}

// ---- Connections (part-to-part route + schedule colour) ----
function emitConnection(cx: DxfCtx, c: Connection, add: (e: DxfEntity) => void): void {
  if (cx.opts.connections && !connectionShown(c, cx.opts.connections)) return;
  const a = cx.parts.get(c.from), b = cx.parts.get(c.to);
  if ((a && cx.hidden(a)) || (b && cx.hidden(b))) return;
  // The same resolved connection the canvas draws: glued / free ends, polygon ends.
  const g = connGeom(cx.drawing, c);
  if (!g) return;
  const color = col(CONNECTION_COLORS[g.discipline]);
  if (g.pipe) {
    // A pipe as the plan draws it: its sides, its centreline, its risers.
    const plan = g.pipe.plan;
    for (const side of plan.sides) if (side.length >= 2) add({ kind: 'polyline', points: side, closed: false, layer: 'PIPES', color });
    if (plan.centre.length >= 2) add({ kind: 'polyline', points: plan.centre, closed: false, layer: 'PIPES-AXIS', color });
    for (const q of plan.risers) add({ kind: 'circle', c: q, r: plan.r, layer: 'PIPES', color });
    return;
  }
  add({ kind: 'polyline', points: g.plan.pts, closed: false, layer: 'CONNECTIONS', color });
}

// ---- Arcs, leaders, revision clouds, floorings ----

/** An arc — the Arc tool's, or a Fillet's round — as a true ARC: both run
 *  counter-clockwise from their start angle to their end angle. */
function emitArc(a: Arc, add: (e: DxfEntity) => void): void {
  if (!(a.radius > 0)) return;
  add({ kind: 'arc', c: a.center, r: a.radius, startDeg: a.startDeg, endDeg: a.endDeg, color: col(a.stroke) });
}

/** A leader's text, in parts of its height: the canvas's 13 px type 4 px clear
 *  of the leader's end, its baseline 5 px over it, a line every 16 px. */
const LEADER_TEXT = { pad: 4 / 13, lift: 5 / 13, step: 16 / 13 };

/** A leader as the plan draws it: its bends, an arrowhead at its first point,
 *  its text at its last — running away from the last bend, the last line just
 *  over the end — `COMMENT_FONT_MM` tall. */
function emitLeader(l: Leader, add: (e: DxfEntity) => void): void {
  const pts = l.points;
  if (pts.length < 2) return;
  const color = col(l.color);
  add({ kind: 'polyline', points: pts, closed: false, color });
  const tip = pts[0], next = pts[1], L = Math.hypot(next.x - tip.x, next.y - tip.y);
  if (L > 1e-9) arrow(tip, { x: (next.x - tip.x) / L, y: (next.y - tip.y) / L }, add, color);
  const end = pts[pts.length - 1], rightward = end.x >= pts[pts.length - 2].x;
  const h = COMMENT_FONT_MM, lines = (l.text || '').split('\n');
  const x = end.x + (rightward ? 1 : -1) * h * LEADER_TEXT.pad;
  lines.forEach((line, i) => {
    if (!line) return;
    const pos = { x, y: end.y + h * (LEADER_TEXT.lift + (lines.length - 1 - i) * LEADER_TEXT.step) };
    add({ kind: 'text', pos, height: h, text: line, color, ...(rightward ? {} : { align: 'right' as const }) });
  });
}

/** A revision cloud as its scallops, each a true ARC — the canvas's rule
 *  (`cloudScallops`) at its own scallop size, or `REVCLOUD_SCALLOP_MM` — in its
 *  colour. */
function emitCloud(c: RevisionCloud, add: (e: DxfEntity) => void): void {
  if (c.points.length < 2) return;
  const color = col(c.color || REVCLOUD_COLOR);
  for (const a of cloudScallops(c.points, c.scallopMm && c.scallopMm > 0 ? c.scallopMm : REVCLOUD_SCALLOP_MM)) {
    add({ kind: 'arc', c: a.center, r: a.radius, startDeg: a.startDeg, endDeg: a.endDeg, color });
  }
}

/** A css colour (`#rrggbb`, or the floor styles' `rgba(r, g, b, a)`) as an ACI. */
function cssAci(css: string): number | undefined {
  const m = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(css.trim());
  return m ? rgbToAci(`#${[m[1], m[2], m[3]].map((v) => Number(v).toString(16).padStart(2, '0')).join('')}`) : col(css);
}

/** A flooring as the plan draws it: its hatch's lines at their world pitch
 *  (`floorHatchLines`) and its stipple's marks (`floorStipple`), its outline, a
 *  sloping floor's fall arrows round its drain (`fallArrows` at
 *  `FALL_ARROW_MM`), then its drain. Not written while the Flooring toggle hides
 *  the floorings. */
function emitFlooring(cx: DxfCtx, f: Flooring, add: (e: DxfEntity) => void): void {
  if (cx.opts.floorings === false || f.points.length < 3) return;
  const style = FLOOR_STYLE[f.kind];
  const line = cssAci(style.line), stroke = cssAci(style.stroke);
  for (const [a, b] of floorHatchLines(f)) add({ kind: 'line', a, b, color: line });
  const marks = floorStipple(f);
  for (const c of marks.dots) add({ kind: 'circle', c, r: marks.r, color: line });
  for (const [a, b] of marks.dashes) add({ kind: 'line', a, b, color: line });
  add({ kind: 'polyline', points: f.points, closed: true, color: stroke });
  const drain = f.drain ? cx.opts.drainOf?.(f.drain) : null;
  if (f.drain && floorIsSloping(f.kind)) {
    for (const { tail, tip } of fallArrows(f.points, f.drain, drain ? Math.max(drain.width, drain.depth) / 2 : null, FALL_ARROW_MM)) {
      add({ kind: 'line', a: tail, b: tip, color: stroke });
      const L = Math.hypot(tail.x - tip.x, tail.y - tip.y) || 1;
      arrow(tip, { x: (tail.x - tip.x) / L, y: (tail.y - tip.y) / L }, add, stroke);
    }
  }
  if (drain) emitDrain(drain, add);
}

/** A stipple mark's size in a DXF (mm): the canvas's px at about 14 mm each
 *  (`FALL_ARROW_MM`'s scale) — a concrete dot r 1.1 px, its dash 4 px, a grout
 *  dot r 0.9 px. */
const STIPPLE_MM = { concreteDot: 15, dash: 55, groutDot: 12 };

/** A floor's stipple at its world pitch, inside it: the canvas's own marks
 *  (`eachStippleMark`) with `FLOOR_HATCH_MM` for their pitch — a sloping
 *  screed's grout dots (every 80 mm), a flat screed's aggregate dots and short
 *  dashes (every 175 mm) — each dot a circle `r` round, every mark wholly inside
 *  the floor (the canvas clips them to it); a tiled floor has none. */
function floorStipple(f: Pick<Flooring, 'kind' | 'points'>): { dots: Vec2[]; dashes: Array<[Vec2, Vec2]>; r: number } {
  const hatch = FLOOR_STYLE[f.kind].hatch, ring = f.points;
  const out = { dots: [] as Vec2[], dashes: [] as Array<[Vec2, Vec2]>, r: hatch === 'grout' ? STIPPLE_MM.groutDot : STIPPLE_MM.concreteDot };
  if (hatch === 'grid' || ring.length < 3) return out;
  const within = (p: Vec2, m: number): boolean => pointInPolygon(p, ring) && ring.every((a, i) => distToSegment(p, a, ring[(i + 1) % ring.length]) > m);
  const xs = ring.map((p) => p.x), ys = ring.map((p) => p.y);
  eachStippleMark(hatch, Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys), FLOOR_HATCH_MM, (x, y, a) => {
    const p = { x, y };
    if (a == null) { if (within(p, out.r)) out.dots.push(p); return; }
    const q = { x: x + Math.cos(a) * STIPPLE_MM.dash, y: y + Math.sin(a) * STIPPLE_MM.dash };
    if (within(p, 0) && within(q, 0)) out.dashes.push([p, q]);
  });
  return out;
}

/** A floor's hatch as lines clipped to it: a tiled floor's grid, a flat screed's
 *  45° lines, every `FLOOR_HATCH_MM` from the origin — none along its outline,
 *  which is drawn anyway. A sloping screed's grout has no lines (`floorStipple`
 *  writes its dots). */
function floorHatchLines(f: Pick<Flooring, 'kind' | 'points'>): Array<[Vec2, Vec2]> {
  const hatch = FLOOR_STYLE[f.kind].hatch, P = FLOOR_HATCH_MM, ring = f.points;
  if (hatch === 'grout' || ring.length < 3) return [];
  const xs = ring.map((p) => p.x), ys = ring.map((p) => p.y);
  const x0 = Math.min(...xs) - 1, x1 = Math.max(...xs) + 1, y0 = Math.min(...ys) - 1, y1 = Math.max(...ys) + 1;
  const out: Array<[Vec2, Vec2]> = [];
  const clip = (a: Vec2, b: Vec2): void => {
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    const at = (t: number): Vec2 => ({ x: a.x + ((b.x - a.x) * t) / L, y: a.y + ((b.y - a.y) * t) / L });
    for (const [t0, t1] of segmentPolyIntervals(a, b, ring)) {
      const mid = at((t0 + t1) / 2);
      if (ring.some((p, i) => distToSegment(mid, p, ring[(i + 1) % ring.length]) < 1e-6)) continue;
      out.push([at(t0), at(t1)]);
    }
  };
  if (hatch === 'grid') {
    for (let k = Math.ceil(x0 / P); k * P <= x1; k++) clip({ x: k * P, y: y0 }, { x: k * P, y: y1 });
    for (let k = Math.ceil(y0 / P); k * P <= y1; k++) clip({ x: x0, y: k * P }, { x: x1, y: k * P });
  } else {
    // x + y = k·P: down to the right on the page, as the canvas draws them.
    for (let k = Math.ceil((x0 + y0) / P); k * P <= x1 + y1; k++) clip({ x: x0, y: k * P - x0 }, { x: x1, y: k * P - x1 });
  }
  return out;
}

/** A floor's drain as the plan outlines it: a drain's rim ring, a channel's
 *  outer and inner rectangles — in their colours. */
function emitDrain(inst: ProductInstance, add: (e: DxfEntity) => void): void {
  const hw = inst.width / 2, hd = inst.depth / 2;
  const rect = (x0: number, y0: number, x1: number, y1: number): Vec2[] =>
    [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map((p) => instanceToWorld(inst, p));
  if (inst.category === 'canalina') {
    const m = Math.min(CANALINA_INSET, hw - 1, hd - 1), color = col(CANALINA_STROKE);
    add({ kind: 'polyline', points: rect(-hw, -hd, hw, hd), closed: true, color });
    add({ kind: 'polyline', points: rect(-hw + m, -hd + m, hw - m, hd - m), closed: true, color });
    return;
  }
  if (hw < 1 || hd < 1) return;
  const c = { x: inst.x, y: inst.y }, color = col(DRAIN_COLOR);
  if (Math.abs(hw - hd) < 1e-6) { add({ kind: 'circle', c, r: hw, color }); return; }
  const major = rotatePoint(hw >= hd ? { x: hw, y: 0 } : { x: 0, y: hd }, inst.rotation);
  add({ kind: 'ellipse', c, majorEnd: major, ratio: Math.min(hw, hd) / Math.max(hw, hd), color });
}

// ---- Imported DXF background ----
function emitBackground(entities: GeomEntity[], offset: Vec2 | undefined, add: (e: DxfEntity) => void): void {
  const ox = offset?.x ?? 0, oy = offset?.y ?? 0;
  const t = (p: Vec2): Vec2 => ({ x: p.x + ox, y: p.y + oy });
  for (const e of entities) {
    const color = col(e.color);
    switch (e.kind) {
      case 'line': add({ kind: 'line', a: t(e.a), b: t(e.b), layer: 'IMPORT', color }); break;
      case 'polyline': add({ kind: 'polyline', points: e.points.map(t), closed: e.closed, layer: 'IMPORT', color }); break;
      case 'circle': add({ kind: 'circle', c: t(e.center), r: e.radius, layer: 'IMPORT', color }); break;
      case 'arc': add({ kind: 'arc', c: t(e.center), r: e.radius, startDeg: e.startAngle, endDeg: e.endAngle, layer: 'IMPORT', color }); break;
      case 'ellipse':
        add({ kind: 'ellipse', c: t(e.center), majorEnd: e.major, ratio: e.ratio, startDeg: (e.start * 180) / Math.PI, endDeg: (e.end * 180) / Math.PI, layer: 'IMPORT', color });
        break;
      case 'spline': {
        const pts = splinePoints(e.control, e.degree, e.knots);
        if (pts && pts.length >= 2) add({ kind: 'polyline', points: pts.map(t), closed: e.closed, layer: 'IMPORT', color });
        else if (e.control.length >= 2) add({ kind: 'polyline', points: e.control.map(t), closed: e.closed, layer: 'IMPORT', color });
        break;
      }
      case 'text': add({ kind: 'text', pos: t(e.pos), height: e.height, text: e.value, align: e.align, baseline: e.baseline, layer: 'IMPORT', color }); break;
      case 'hatch': {
        const loops = e.loops.map((l) => l.map(t));
        add({ kind: 'hatch', loops, layer: 'IMPORT', color });
        for (const l of loops) add({ kind: 'polyline', points: l, closed: true, layer: 'IMPORT', color });
        break;
      }
    }
  }
}

// ---- User polygons ----
function emitPolygon(poly: Polygon, layer: string, add: (e: DxfEntity) => void): void {
  if (poly.points.length < 2) return;
  const stroke = col(poly.stroke);
  const fill = col(poly.fill);
  if (poly.fillOpacity > 0.001 && poly.points.length >= 3 && fill != null) {
    add({ kind: 'hatch', loops: [poly.points, ...(poly.holes || [])], layer, color: fill });
  }
  // An ellipse/circle shape carries a tessellated ring; recover the true ellipse
  // so the outline stays a smooth, editable curve.
  const el = poly.ellipse && !(poly.holes && poly.holes.length) ? ellipseFromRing(poly.points) : null;
  if (el) { add({ kind: 'ellipse', c: el.c, majorEnd: el.majorEnd, ratio: el.ratio, layer, color: stroke }); return; }
  add({ kind: 'polyline', points: poly.points, closed: true, layer, color: stroke });
  for (const hole of poly.holes || []) if (hole.length >= 3) add({ kind: 'polyline', points: hole, closed: true, layer, color: stroke });
}

/** Recover a DXF ellipse (centre, major-axis vector, minor/major ratio) from a
 *  ring produced by the ellipse tool: the centroid is the centre, points[0] and
 *  points[n/4] are the two axis tips. */
function ellipseFromRing(pts: Vec2[]): { c: Vec2; majorEnd: Vec2; ratio: number } | null {
  const n = pts.length;
  if (n < 4) return null;
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  const a0 = { x: pts[0].x - cx, y: pts[0].y - cy };
  const aq = { x: pts[Math.round(n / 4) % n].x - cx, y: pts[Math.round(n / 4) % n].y - cy };
  const l0 = Math.hypot(a0.x, a0.y), lq = Math.hypot(aq.x, aq.y);
  const majLen = Math.max(l0, lq);
  if (majLen < 1e-6) return null;
  return { c: { x: cx, y: cy }, majorEnd: l0 >= lq ? a0 : aq, ratio: Math.min(l0, lq) / majLen };
}

// ---- User walls ----
function emitWall(wall: Wall, add: (e: DxfEntity) => void, cuts: readonly InWallPart[] = []): void {
  if (wall.points.length < 2) return;
  const fill = col(wall.fill);
  if (wall.style === 'line') {
    add({ kind: 'polyline', points: wall.points, closed: false, layer: 'WALLS', color: fill });
    return;
  }
  // The band cut at its doors, panes and openings, as the plan draws it.
  for (const piece of wallSolidRuns(wall)) {
    if (piece[0].length < 3) continue;
    add({ kind: 'hatch', loops: piece, layer: 'WALLS', color: fill });
    for (const loop of piece) add({ kind: 'polyline', points: loop, closed: true, layer: 'WALLS', color: col(wall.stroke) ?? fill });
  }
  for (const sp of wallFeatureSpans(wall)) {
    if (sp.f.kind === 'door') {
      const sw = wallDoorSwing(sp);
      add({ kind: 'line', a: sw.hinge, b: sw.open, layer: 'DOORS' });
      emitSwing(sw, add);
    } else if (sp.f.kind === 'glass') {
      const pane = wallPaneQuad(sp);
      add({ kind: 'hatch', loops: [pane], layer: 'GLASS', color: rgbToAci(sp.f.color ?? '#8fd0ff') });
      add({ kind: 'polyline', points: pane, closed: true, layer: 'GLASS' });
    } else {
      add({ kind: 'polyline', points: wallFeatureQuad(sp), closed: true, layer: 'GLASS' });
    }
  }
  // A part's pocket (its flush box): its outline in the wall, as the plan draws it.
  for (const pk of wallPockets(wall, cuts)) {
    const fr = wallSegFrame(wall, pk.seg);
    if (fr) add({ kind: 'polyline', points: wallPocketQuad(fr, pk), closed: true, layer: 'WALLS', color: col(wall.stroke) ?? fill, ltype: 'DASHED' });
  }
}

// ---- Instances (products, rooms, poly-rooms, node instances) ----
function emitInstance(inst: ProductInstance, add: (e: DxfEntity) => void, cx: DxfCtx): void {
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const wl = (pts: Vec2[]): Vec2[] => pts.map(W);

  const room = !!inst.autoBenches;
  if (room && isPolyRoom(inst)) {
    // Polygonal room: wall band fill + outline, benches, door.
    const pw = polyWall(inst);
    const wallCol = col(inst.wallColor);
    for (const quad of pw.fill) if (quad.length >= 3) add({ kind: 'hatch', loops: [wl(quad)], layer: 'WALLS', color: wallCol });
    for (const [a, b] of [...pw.outer, ...pw.inner, ...pw.jambs]) add({ kind: 'line', a: W(a), b: W(b), layer: 'WALLS' });
    // Rounded corners as true ARCs, so the curve stays a curve in CAD.
    for (const arc of pw.cornerArcs) {
      if (!(arc.radius > 0)) continue;
      const c = W(arc.center), f = W(arc.from), t = W(arc.to);
      const deg = (p: Vec2): number => (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI;
      let s0 = deg(f), e0 = deg(t);
      // DXF arcs always run counter-clockwise from start to end.
      if (((e0 - s0) % 360 + 360) % 360 > 180) { const tmp = s0; s0 = e0; e0 = tmp; }
      add({ kind: 'arc', c, r: arc.radius, startDeg: s0, endDeg: e0, layer: 'WALLS', color: wallCol });
    }
    // Interior partitions (doors cut out) on the WALLS layer, in their colour.
    for (const iw of inst.innerWalls || []) {
      if (iw.edge == null) continue;
      for (const quad of polyInnerWallFill(inst, iw)) {
        if (quad.length < 3) continue;
        add({ kind: 'hatch', loops: [wl(quad)], layer: 'WALLS', color: col(iw.color ?? inst.wallColor) });
        add({ kind: 'polyline', points: wl(quad), closed: true, layer: 'WALLS' });
      }
    }
    // Hidden heater walls go on the WALLS layer in their own colour.
    for (const hb of heaterWallBands(inst)) {
      if (hb.poly.length < 3) continue;
      add({ kind: 'hatch', loops: [wl(hb.poly)], layer: 'WALLS', color: col(hb.wall.color) });
      add({ kind: 'polyline', points: wl(hb.poly), closed: true, layer: 'WALLS' });
    }
    // The benches as the plan draws them, in its order (`roomBenchSeats`):
    // a slatted seat's trestles filled under it; each slat or band one hatch
    // in the bench's own colour, its holes among its loops, and its outline;
    // a plain band's lip over it. Then every trestle's outline, dashed, and
    // the closed ends' lines. R12 has no line weight: the canvas's px-wide
    // strokes are hairlines, the lip told from the outline under it by its
    // shade; the trestles' 4 / 3 px dash is DASHED, the file's one dashed
    // type (100 / 50 mm), which every dashed plan mark here takes.
    const seats = roomBenchSeats(inst);
    for (const s of seats) {
      for (const t of s.trestles) add({ kind: 'hatch', loops: [wl(t.poly)], layer: 'BENCHES', color: col(structureColor(s.fill)) });
      const color = col(s.color ?? inst.benchColor);
      for (const r of s.rings) {
        add({ kind: 'hatch', loops: [wl(r.outer), ...r.holes.map(wl)], layer: 'BENCHES', color });
        for (const ring of [r.outer, ...r.holes]) add({ kind: 'polyline', points: wl(ring), closed: true, layer: 'BENCHES' });
      }
      if (s.lip) add({ kind: 'line', a: W(s.lip[0]), b: W(s.lip[1]), layer: 'BENCHES', color: col(darkenHex(s.fill, LIP_SHADE)) });
    }
    for (const s of seats) {
      for (const t of s.trestles) add({ kind: 'polyline', points: wl(t.poly), closed: true, layer: 'BENCHES', color: col(darkenHex(s.fill, TRESTLE_LINE_SHADE)), ltype: 'DASHED' });
    }
    for (const line of closedEndLines(inst)) add({ kind: 'polyline', points: wl(line), layer: 'BENCHES', color: col(BENCH_END_LINE) });
    // The floor grille: its top slats, its battens under them dashed.
    const grille = grilleLayout(inst);
    if (grille) {
      for (const p of grille.slats) for (const ring of [p.ring.outer, ...p.ring.holes]) add({ kind: 'polyline', points: wl(ring), closed: true, layer: 'GRILLE' });
      for (const p of grille.battens) add({ kind: 'polyline', points: wl(p.ring.outer), closed: true, layer: 'GRILLE', ltype: 'DASHED' });
    }
    // Backrests go with the benches, over them.
    for (const br of backrestsOf(inst)) {
      for (const band of backrestPlan(inst, br)) {
        add({ kind: 'hatch', loops: [wl(band)], layer: 'BENCHES', color: col(backrestColor(inst, br)) });
        add({ kind: 'polyline', points: wl(band), closed: true, layer: 'BENCHES' });
      }
    }
    for (const door of roomDoors(inst)) {
      const rd = roomDoor(inst, door);
      if (rd?.kind !== 'plain') continue; // a glazing's door is drawn with it (emitGlazings)
      const sw = mapSwing(rd.swing, W);
      add({ kind: 'line', a: sw.hinge, b: sw.open, layer: 'DOORS' });
      add({ kind: 'line', a: sw.hinge, b: sw.closed, layer: 'DOORS' });
      emitSwing(sw, add);
    }
    emitOpenings(inst, W, wl, add);
    emitGlazings(inst, W, wl, add);
    // The holes the parts set into its walls cut through them, as the plan draws them.
    for (const seg of roomWallCuts(inst, cx.cuts)) add({ kind: 'polyline', points: wl(openingHoleBand(inst, seg)), closed: true, layer: 'WALLS', ltype: 'DASHED' });
    // The water risers in its walls, of the parts shown, as the plan draws them.
    for (const rz of cx.risers) {
      const part = cx.parts.get(rz.src);
      if (rz.room !== inst.id || !part || cx.hidden(part)) continue;
      const color = RISER_ACI[rz.water];
      add({ kind: 'hatch', loops: [riserRing(rz)], layer: 'PRODUCTS', color });
      add({ kind: 'circle', c: rz.at, r: rz.r, layer: 'PRODUCTS', color });
    }
    if (cx.opts.showCeilings && !isFlatCeiling(inst)) emitCeiling(inst, W, add);
    if (cx.lit) emitLights(inst, W, cx.lit, add);
    labelInstance(inst, cx.names, add);
    return;
  }

  emitPart(inst, add);
  labelInstance(inst, cx.names, add);
}

/** The plan's clearance line (render.ts), opaque. */
const GAP_LINE = '#788296';

/** A placed part as its look has it (partLook.ts), as the plan draws it: what
 *  stands round its body, then its body — a run its bands; tilted, its true
 *  projection; upright, its own drawing, else its model's lines from above, else
 *  its footprint (a heater block's in its body colour). */
function emitPart(inst: ProductInstance, add: (e: DxfEntity) => void): void {
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const look = partLook(inst);
  if (look.body.kind === 'run') { if (look.plan.symbol === 'saunaBench') emitSaunaBench(inst, add); else emitRun(inst, add); return; }
  for (const a of look.attachments) emitAttachment(a, W, add);

  // A tilted part: its true projection, as the plan draws it — its 3D model's
  // lines seen from above (partModel.ts), or its box's faces turned up with its
  // own drawing laid on the one that faces up.
  if (isTilted(inst)) {
    const b = bodyOf(inst, 0, { w: inst.width, d: inst.depth, h: partHeight(inst) });
    const model = modelPlan(inst, b);
    if (model.length) {
      for (const l of modelPlanLines(model)) add({ kind: 'polyline', points: l.pts, closed: false, layer: 'PRODUCTS', color: col(l.color) });
    } else {
      for (const f of planFaces(b)) add({ kind: 'polyline', points: f, closed: true, layer: 'PRODUCTS', color: col(inst.color) });
      if (inst.geometry) {
        const z = applyMat3(b.m, { x: 0, y: 0, z: 1 }).z >= 0 ? b.size.h : 0;
        const laid = (p: Vec2): Vec2 => { const q = bodyPoint(b, { x: p.x, y: p.y, z }); return { x: q.x, y: q.y }; };
        for (const e of inst.geometry.entities) emitLaidGeom(e, laid, add);
      }
    }
    return;
  }

  // Its own detailed drawing (local frame), whatever its symbol: a DXF has the
  // lines a symbol only stands for.
  if (look.plan.drawing && inst.geometry) {
    for (const e of inst.geometry.entities) emitLocalGeom(e, W, inst.rotation, add);
    return;
  }

  const footprint = (color: string | undefined): void => {
    const corners = instanceCorners(inst);
    if (col(color) != null) add({ kind: 'hatch', loops: [corners], layer: 'PRODUCTS', color: col(color) });
    add({ kind: 'polyline', points: corners, closed: true, layer: 'PRODUCTS', color: col(color) });
  };
  const symbol = look.plan.symbol;
  switch (symbol) {
    // A heater block, or a part shown as its model: that model's lines seen from
    // above, as the plan draws it — a heater block without one its filled body.
    case 'heaterBlock':
    case 'model': {
      const model = uprightModelPlan(inst);
      if (model.length) for (const l of modelPlanLines(model)) add({ kind: 'polyline', points: l.pts, closed: false, layer: 'PRODUCTS', color: col(l.color) });
      else footprint(symbol === 'heaterBlock' ? inst.color || HEATER_BODY_COLOR : inst.color);
      return;
    }
    // A symbol a DXF has no lines for, and a plain part: its footprint.
    case 'box':
    case 'canalina':
    case 'drain':
    case 'drawing':
    case 'flex4':
    case 'heatedBench':
    case 'hotTub':
    case 'infrared':
    case 'lighting':
    case 'saltWall':
    case 'steamCube':
      footprint(inst.color);
      return;
    case 'saunaBench':
      emitSaunaBench(inst, add);
      return;
    default: unreachable(symbol, 'plan symbol');
  }
}

/** A standalone sauna bench as the plan draws it (`saunaBenchPlan`, which the
 *  plan reads too), with a room bench's marks: its trestles filled under it;
 *  per segment its slats, each a hatch in its colour and its outline (a bench
 *  with none its ribbon), and the ribbon's outline; then the trestles' dashed
 *  outlines and its closed ends' lines. */
function emitSaunaBench(inst: ProductInstance, add: (e: DxfEntity) => void): void {
  const plan = saunaBenchPlan(inst);
  const color = col(inst.color);
  for (const t of plan.trestles) add({ kind: 'hatch', loops: [t.poly], layer: 'PRODUCTS', color: col(structureColor(inst.color)) });
  for (const seg of plan.segments) {
    for (const ring of seg.slats.length ? seg.slats : [seg.body]) {
      add({ kind: 'hatch', loops: [ring], layer: 'PRODUCTS', color });
      add({ kind: 'polyline', points: ring, closed: true, layer: 'PRODUCTS' });
    }
    if (seg.slats.length) add({ kind: 'polyline', points: seg.body, closed: true, layer: 'PRODUCTS' });
  }
  for (const t of plan.trestles) add({ kind: 'polyline', points: t.poly, closed: true, layer: 'PRODUCTS', color: col(darkenHex(inst.color, TRESTLE_LINE_SHADE)), ltype: 'DASHED' });
  for (const line of plan.ends) add({ kind: 'polyline', points: line, layer: 'PRODUCTS', color: col(BENCH_END_LINE) });
}

/** A node run (salt wall, heated bench): its real band, a filled quad per
 *  segment (a salt wall's its frame footprint; a bench offsets the node line by
 *  its depth), matching the drawing. */
function emitRun(inst: ProductInstance, add: (e: DxfEntity) => void): void {
  const nodes = inst.nodes ?? [];
  const salt = inst.category === 'saltwall';
  const color = col(inst.color);
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    let quad: Vec2[];
    if (salt) {
      quad = saltWallHull(a, b);
    } else {
      const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy) || 1;
      const nx = dy / L, ny = -dx / L, d = inst.depth; // body-side normal (matches the renderer)
      quad = [a, b, { x: b.x + nx * d, y: b.y + ny * d }, { x: a.x + nx * d, y: a.y + ny * d }];
    }
    if (quad.length >= 3) {
      add({ kind: 'hatch', loops: [quad], layer: 'PRODUCTS', color });
      add({ kind: 'polyline', points: quad, closed: true, layer: 'PRODUCTS', color });
    }
  }
}

/** What stands round a part's body, as the plan draws it: its guard cover's
 *  bands filled in its wood, outlined round their union; its clearance gap a
 *  closed line. */
function emitAttachment(a: PartAttachment, W: (p: Vec2) => Vec2, add: (e: DxfEntity) => void): void {
  switch (a.kind) {
    case 'cover': {
      const rings = a.bands.map((b) => [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }]);
      for (const r of rings) add({ kind: 'hatch', loops: [r.map(W)], layer: 'PRODUCTS', color: col(a.color) });
      for (const [p, q] of wallBoundaryEdges(rings)) add({ kind: 'line', a: W(p), b: W(q), layer: 'PRODUCTS', color: col(a.color) });
      return;
    }
    case 'gap': {
      const { x0, y0, x1, y1 } = a.box;
      add({ kind: 'polyline', points: [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }].map(W), closed: true, layer: 'PRODUCTS', color: col(GAP_LINE) });
      return;
    }
    default: unreachable(a, 'attachment');
  }
}

/** A shaped ceiling's plan lines and fall arrows, as Show ceilings draws them on
 *  every room (never the crown figure or a grip). A 0 mm ceiling has none. */
function emitCeiling(inst: ProductInstance, W: (p: Vec2) => Vec2, add: (e: DxfEntity) => void): void {
  const m = ceilingMarksShown(inst, { all: true, selected: false, single: false });
  for (const l of m.lines) if (l.pts.length >= 2) add({ kind: 'polyline', points: l.pts.map(W), closed: !!l.closed, layer: 'CEILING' });
  for (const a of m.arrows) {
    const h = CEILING_ARROW_MM / 2;
    const tail = W({ x: a.at.x - a.dir.x * h, y: a.at.y - a.dir.y * h }), tip = W({ x: a.at.x + a.dir.x * h, y: a.at.y + a.dir.y * h });
    add({ kind: 'line', a: tail, b: tip, layer: 'CEILING' });
    const L = Math.hypot(tail.x - tip.x, tail.y - tip.y) || 1;
    arrow(tip, { x: (tail.x - tip.x) / L, y: (tail.y - tip.y) / L }, add, undefined, 'CEILING');
  }
}

/** A room's lights on its LIGHTS layer — dashed, overhead, in the lights' amber —
 *  as the plan draws them: a spot's or a fibre end's ring a true CIRCLE, a cove's
 *  second layer or ledges grey; each tag beside its light. */
function emitLights(inst: ProductInstance, W: (p: Vec2) => Vec2, lit: LightExport, add: (e: DxfEntity) => void): void {
  for (const l of lightLines(inst, lit.parts)) {
    const color = l.ceiling ? { color: 8 } : {};
    if (l.ring) { add({ kind: 'circle', c: W(l.ring.c), r: l.ring.r, layer: 'LIGHTS', ...color }); continue; }
    const points = l.pts.map(W);
    add(points.length === 2 && !l.closed ? { kind: 'line', a: points[0], b: points[1], layer: 'LIGHTS', ...color } : { kind: 'polyline', points, closed: l.closed, layer: 'LIGHTS', ...color });
  }
  for (const a of lightTagAnchors(inst)) {
    const tag = lit.tags.get(a.key);
    if (!tag) continue;
    add({ kind: 'text', pos: W(tagTextAt(a)), height: 100, text: tag, align: 'center', baseline: 'middle', layer: 'LIGHTS' });
    const leader = tagLeader(a);
    if (leader) add({ kind: 'line', a: W(leader[0]), b: W(leader[1]), layer: 'LIGHTS' });
  }
}

/** Glass panes (filled) and openings (outline) of a room, in world coords.
 *  Works for both rectangular and polygonal rooms (the glass/opening helpers
 *  dispatch on `seg.edge`). */
function emitOpenings(inst: ProductInstance, W: (p: Vec2) => Vec2, wl: (pts: Vec2[]) => Vec2[], add: (e: DxfEntity) => void): void {
  for (const seg of inst.glass || []) {
    if (glazingOfPane(inst, seg.id)) continue;
    for (const poly of glassPolysLocal(inst, seg)) {
      if (poly.length < 3) continue;
      add({ kind: 'hatch', loops: [wl(poly)], layer: 'GLASS', color: rgbToAci(seg.color ?? '#8fd0ff') });
      add({ kind: 'polyline', points: wl(poly), closed: true, layer: 'GLASS' });
    }
  }
  for (const seg of inst.openings || []) {
    const band = openingBandLocal(inst, seg);
    if (band.length >= 3) add({ kind: 'polyline', points: wl(band), closed: true, layer: 'GLASS' });
  }
}

/** A room's glazings as the plan draws them (glazingPlan.ts): the sections of
 *  their profiles, glass and seals at the plan's cut, and the door's swing
 *  about its hinges' axis. */
function emitGlazings(inst: ProductInstance, W: (p: Vec2) => Vec2, wl: (pts: Vec2[]) => Vec2[], add: (e: DxfEntity) => void): void {
  for (const g of roomGlazings(inst)) {
    for (const c of glazingPlanCut(g)) {
      const layer = c.stuff === 'glass' ? 'GLASS' : 'DOORS';
      const color = c.stuff === 'wood' ? col(inst.wallColor) : rgbToAci(glazingLook(g, c.stuff).color);
      if (c.exact) add({ kind: 'hatch', loops: c.rings.map(wl), layer, color });
      for (const r of c.rings) add({ kind: 'polyline', points: wl(r), closed: true, layer });
    }
    const gs = glazingSwing(g);
    if (gs) {
      const sw = mapSwing(gs, W);
      add({ kind: 'line', a: sw.hinge, b: sw.open, layer: 'DOORS' });
      emitSwing(sw, add);
    }
  }
}

/** A GeomEntity in an instance's local frame → world DXF entities. The instance
 *  transform is rigid (rotate `rot`° + translate), so circles/arcs/ellipses stay
 *  true curves — emit them as such rather than tessellating. */
function emitLocalGeom(e: GeomEntity, W: (p: Vec2) => Vec2, rot: number, add: (e: DxfEntity) => void): void {
  const color = col(e.color);
  switch (e.kind) {
    case 'line': add({ kind: 'line', a: W(e.a), b: W(e.b), layer: 'PRODUCTS', color }); break;
    case 'polyline': add({ kind: 'polyline', points: e.points.map(W), closed: e.closed, layer: 'PRODUCTS', color }); break;
    case 'circle': add({ kind: 'circle', c: W(e.center), r: e.radius, layer: 'PRODUCTS', color }); break;
    case 'arc': add({ kind: 'arc', c: W(e.center), r: e.radius, startDeg: e.startAngle + rot, endDeg: e.endAngle + rot, layer: 'PRODUCTS', color }); break;
    case 'ellipse': {
      const c = W(e.center), tip = W({ x: e.center.x + e.major.x, y: e.center.y + e.major.y });
      add({ kind: 'ellipse', c, majorEnd: { x: tip.x - c.x, y: tip.y - c.y }, ratio: e.ratio, startDeg: (e.start * 180) / Math.PI, endDeg: (e.end * 180) / Math.PI, layer: 'PRODUCTS', color });
      break;
    }
    case 'spline': { const pts = splinePoints(e.control, e.degree, e.knots) || e.control; if (pts.length >= 2) add({ kind: 'polyline', points: pts.map(W), closed: e.closed, layer: 'PRODUCTS', color }); break; }
    case 'text': add({ kind: 'text', pos: W(e.pos), height: e.height, text: e.value, align: e.align, baseline: e.baseline, layer: 'PRODUCTS', color }); break;
    case 'hatch': for (const l of e.loops) add({ kind: 'polyline', points: l.map(W), closed: true, layer: 'PRODUCTS', color }); break;
  }
}

/** A drawing's entity through a map that need not keep circles round (a tilted
 *  part's drawing laid on its face): curves as polylines through mapped points. */
function emitLaidGeom(e: GeomEntity, W: (p: Vec2) => Vec2, add: (e: DxfEntity) => void): void {
  const color = col(e.color);
  const poly = (pts: Vec2[], closed: boolean): void => { if (pts.length >= 2) add({ kind: 'polyline', points: pts.map(W), closed, layer: 'PRODUCTS', color }); };
  const arc = (c: Vec2, ax: Vec2, ay: Vec2, a0: number, a1: number): Vec2[] => {
    const n = Math.max(8, Math.ceil(Math.abs(a1 - a0) / (Math.PI / 24)));
    return Array.from({ length: n + 1 }, (_, i) => { const a = a0 + ((a1 - a0) * i) / n; return { x: c.x + ax.x * Math.cos(a) + ay.x * Math.sin(a), y: c.y + ax.y * Math.cos(a) + ay.y * Math.sin(a) }; });
  };
  switch (e.kind) {
    case 'line': add({ kind: 'line', a: W(e.a), b: W(e.b), layer: 'PRODUCTS', color }); break;
    case 'polyline': poly(e.points, e.closed); break;
    case 'circle': poly(arc(e.center, { x: e.radius, y: 0 }, { x: 0, y: e.radius }, 0, 2 * Math.PI).slice(0, -1), true); break;
    case 'arc': {
      const a0 = (e.startAngle * Math.PI) / 180, a1 = (e.endAngle * Math.PI) / 180;
      poly(arc(e.center, { x: e.radius, y: 0 }, { x: 0, y: e.radius }, a0, a1 > a0 ? a1 : a1 + 2 * Math.PI), false);
      break;
    }
    case 'ellipse': {
      const minor = { x: -e.major.y * e.ratio, y: e.major.x * e.ratio };
      const full = Math.abs(e.end - e.start) >= 2 * Math.PI - 1e-9;
      poly(arc(e.center, e.major, minor, e.start, e.end > e.start ? e.end : e.end + 2 * Math.PI).slice(0, full ? -1 : undefined), full);
      break;
    }
    case 'spline': { const pts = splinePoints(e.control, e.degree, e.knots) || e.control; poly(pts, e.closed); break; }
    case 'text': add({ kind: 'text', pos: W(e.pos), height: e.height, text: e.value, align: e.align, baseline: e.baseline, layer: 'PRODUCTS', color }); break;
    case 'hatch': for (const l of e.loops) poly(l, true); break;
  }
}

/** A part's label at its centre: its auto-numbered name, as on screen. */
function labelInstance(inst: ProductInstance, names: ReadonlyMap<string, string>, add: (e: DxfEntity) => void): void {
  const label = names.get(inst.id) ?? inst.name;
  if (!label) return;
  add({ kind: 'text', pos: { x: inst.x, y: inst.y }, height: 150, text: label, layer: 'TEXT' });
}

// ---- Text comments ----
function emitText(t: TextComment, add: (e: DxfEntity) => void): void {
  const h = t.heightMm ?? COMMENT_FONT_MM; // model-space text height (mm)
  // MTEXT (top-left anchored, wrapped to the box) keeps multi-line comments intact.
  add({ kind: 'mtext', pos: textBoxToWorld(t, 20, 20), height: h, width: Math.max(0, t.w - 40), text: t.text, layer: 'TEXT', color: col(t.color), rotation: t.rotation });
  // Box + optional leader, both in the box's (possibly turned) frame.
  add({ kind: 'polyline', points: textBoxCorners(t), closed: true, layer: 'TEXT', color: col(t.color) });
  if (t.leader) add({ kind: 'line', a: textBoxToWorld(t, 0, t.h / 2), b: t.leader, layer: 'TEXT', color: col(t.color) });
}

/** A CCW ARC through the ≤180° sweep from `from` to `to` about `centre`. */
/** A door's swing arc (doorSwing.ts), when it has one. */
function emitSwing(sw: DoorSwing, add: (e: DxfEntity) => void): void {
  const arc = swingDxfArc(sw);
  if (arc) add(arc);
}

// ---- Dimensions → exploded geometry (extension + dimension lines, arrows, label).
//      R12 dimension objects need a DIMSTYLE + anonymous block; exploding to plain
//      lines/text is simpler and displays identically in every reader. ----
function emitDimension(d: DimensionLine, style: DimStyle, add: (e: DxfEntity) => void): void {
  const color = col(d.color);
  const dx = d.b.x - d.a.x, dy = d.b.y - d.a.y;
  const len = Math.hypot(dx, dy);
  if (len < 1e-6) return;
  const ux = dx / len, uy = dy / len, nx = -uy, ny = ux, off = d.offset;
  const a2 = { x: d.a.x + nx * off, y: d.a.y + ny * off };
  const b2 = { x: d.b.x + nx * off, y: d.b.y + ny * off };
  const label = formatDimension(len, style); // the same figure the drawing shows
  const mid = { x: (a2.x + b2.x) / 2 + nx * 40, y: (a2.y + b2.y) / 2 + ny * 40 };
  let rot = (Math.atan2(uy, ux) * 180) / Math.PI;
  if (rot > 90 || rot < -90) rot += 180; // keep text upright

  if (Math.abs(off) > 0.01) {
    add({ kind: 'line', a: d.a, b: a2, layer: 'DIMENSIONS', color });
    add({ kind: 'line', a: d.b, b: b2, layer: 'DIMENSIONS', color });
  }
  add({ kind: 'line', a: a2, b: b2, layer: 'DIMENSIONS', color });
  arrow(a2, { x: ux, y: uy }, add, color);
  arrow(b2, { x: -ux, y: -uy }, add, color);
  add({ kind: 'text', pos: mid, height: 120, text: label, rotationDeg: rot, layer: 'DIMENSIONS', color });
}

/** Two short lines forming an arrowhead at `tip`, opening along `dir`. */
function arrow(tip: Vec2, dir: Vec2, add: (e: DxfEntity) => void, color?: number, layer = 'DIMENSIONS'): void {
  const size = 45, ang = 0.4;
  const rot = (a: number): Vec2 => ({ x: dir.x * Math.cos(a) - dir.y * Math.sin(a), y: dir.x * Math.sin(a) + dir.y * Math.cos(a) });
  const l = rot(ang), r = rot(-ang);
  add({ kind: 'line', a: tip, b: { x: tip.x + l.x * size, y: tip.y + l.y * size }, layer, color });
  add({ kind: 'line', a: tip, b: { x: tip.x + r.x * size, y: tip.y + r.y * size }, layer, color });
}
